import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DEAL_STAGE_CATEGORY_LABELS, DEAL_STAGE_CATEGORY_ORDER, stageCategoryOf } from "@/enum/DealStageCategory";
import { useConversionToast } from "@/hooks/useConversionToast";
import { getContactDisplayName } from "@/util/string";
import {
  useCreateDealMutation,
  useUpdateDealMutation,
  useGetDealStagesQuery,
  useGetMembersQuery,
} from "@/store/rest_api";
import { toast } from "sonner";
import Contact from "@/model/interface/Contact";
import Deal from "@/model/interface/Deal";
import DealStage from "@/model/interface/DealStage";
import Membership from "@/model/interface/Membership";
import Property from "@/model/interface/Property";
import PropertyCombobox from "@/components/pages/properties/PropertyCombobox";
import ContactCombobox from "@/components/contacts/ContactCombobox";
import { memberName } from "@/util/member";
import { Loader2 } from "lucide-react";

const dealSchema = z.object({
  stageId: z.string().optional(),
  assignedMembershipId: z.string().optional(),
  offerPrice: z.coerce.number().nonnegative().optional().or(z.literal("")),
  arv: z.coerce.number().nonnegative().optional().or(z.literal("")),
  repairEstimate: z.coerce.number().nonnegative().optional().or(z.literal("")),
  assignmentFee: z.coerce.number().nonnegative().optional().or(z.literal("")),
  closingDate: z.string().optional(),
  profit: z.coerce.number().optional().or(z.literal("")),
  source: z.string().optional(),
  notes: z.string().optional(),
});

type DealFormValues = z.infer<typeof dealSchema>;

interface DealFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal?: Deal;
  defaultContact?: Contact;
}

export function DealFormDialog({ open, onOpenChange, deal, defaultContact }: DealFormDialogProps) {
  const isEditing = !!deal;

  const [selectedProperty, setSelectedProperty] = React.useState<Property | null>(deal?.property ?? null);
  const [selectedContact, setSelectedContact] = React.useState<Contact | null>(deal?.contact ?? defaultContact ?? null);

  const { data: stages = [] as DealStage[] } = useGetDealStagesQuery({});
  const { data: members = [] as Membership[] } = useGetMembersQuery({});

  const [createDeal, { isLoading: creating }] = useCreateDealMutation();
  const [updateDeal, { isLoading: updating }] = useUpdateDealMutation();
  const showConversionToast = useConversionToast();

  const form = useForm<DealFormValues>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      stageId: "",
      assignedMembershipId: "",
      offerPrice: "",
      arv: "",
      repairEstimate: "",
      assignmentFee: "",
      closingDate: "",
      profit: "",
      source: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (open) {
      setSelectedProperty(deal?.property ?? null);
      setSelectedContact(deal?.contact ?? defaultContact ?? null);
      if (deal) {
        form.reset({
          stageId: deal.stage?.id || "",
          assignedMembershipId: deal.assignedMembership?.id?.toString() || "",
          offerPrice: deal.offerPrice ?? "",
          arv: deal.arv ?? "",
          repairEstimate: deal.repairEstimate ?? "",
          assignmentFee: deal.assignmentFee ?? "",
          closingDate: deal.closingDate || "",
          profit: deal.profit ?? "",
          source: deal.source || "",
          notes: deal.notes || "",
        });
      } else {
        form.reset();
      }
    }
  }, [deal, open, defaultContact?.id]);

  const onSubmit = async (values: DealFormValues) => {
    const payload: any = {
      property: selectedProperty ? { id: selectedProperty.id } : undefined,
      offerPrice: values.offerPrice || null,
      arv: values.arv || null,
      repairEstimate: values.repairEstimate || null,
      assignmentFee: values.assignmentFee || null,
      closingDate: values.closingDate || null,
      profit: values.profit || null,
      source: values.source || null,
      notes: values.notes || null,
    };

    if (selectedContact?.id) {
      payload.contact = { id: selectedContact.id };
    }
    if (values.stageId) payload.stage = { id: values.stageId };
    if (values.assignedMembershipId) payload.assignedMembership = { id: parseInt(values.assignedMembershipId) };

    try {
      const saved = isEditing
        ? await updateDeal({ id: deal.id, data: payload }).unwrap()
        : await createDeal(payload).unwrap();
      toast.success(isEditing ? "Deal updated" : "Deal created");
      showConversionToast(saved.convertedContactId, getContactDisplayName(saved.contact ?? selectedContact));
      onOpenChange(false);
    } catch {
      toast.error(isEditing ? "Failed to update deal" : "Failed to create deal");
    }
  };

  const isSubmitting = creating || updating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Deal" : "New Deal"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact</Label>
                <ContactCombobox value={selectedContact} onChange={setSelectedContact} />
              </div>
              <FormField control={form.control} name="stageId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Stage</FormLabel>
                  <Select value={field.value?.toString()} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Default stage" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {DEAL_STAGE_CATEGORY_ORDER.map((category) => {
                        const categoryStages = stages.filter((stage) => stageCategoryOf(stage) === category);
                        if (categoryStages.length === 0) {
                          return null;
                        }
                        return (
                          <SelectGroup key={category}>
                            <SelectLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                              {DEAL_STAGE_CATEGORY_LABELS[category]}
                            </SelectLabel>
                            {categoryStages.map((stage) => (
                              <SelectItem key={stage.id} value={stage.id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                                  {stage.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </FormItem>
              )} />
            </div>

            <div className="space-y-2">
              <Label>Property</Label>
              <PropertyCombobox value={selectedProperty} onChange={setSelectedProperty} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="offerPrice" render={({ field }) => (
                <FormItem>
                  <FormLabel>Offer Price</FormLabel>
                  <FormControl><Input type="number" placeholder="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="arv" render={({ field }) => (
                <FormItem>
                  <FormLabel>ARV</FormLabel>
                  <FormControl><Input type="number" placeholder="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="repairEstimate" render={({ field }) => (
                <FormItem>
                  <FormLabel>Repair Estimate</FormLabel>
                  <FormControl><Input type="number" placeholder="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="assignmentFee" render={({ field }) => (
                <FormItem>
                  <FormLabel>Assignment Fee</FormLabel>
                  <FormControl><Input type="number" placeholder="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="profit" render={({ field }) => (
                <FormItem>
                  <FormLabel>Profit</FormLabel>
                  <FormControl><Input type="number" placeholder="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="closingDate" render={({ field }) => (
                <FormItem>
                  <FormLabel>Closing Date</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="assignedMembershipId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Assigned To</FormLabel>
                  <Select value={field.value?.toString()} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {members.map((m) => (
                        <SelectItem key={m.id} value={m.id.toString()}>
                          {memberName(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )} />
              <FormField control={form.control} name="source" render={({ field }) => (
                <FormItem>
                  <FormLabel>Source</FormLabel>
                  <FormControl><Input placeholder="e.g. Cold Call, Direct Mail" {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl><Textarea rows={3} {...field} /></FormControl>
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
                {isEditing ? "Update Deal" : "Create Deal"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
